import type { TextFieldProps } from "@mui/material";
import { Box, TextField } from "@mui/material";
import type { FieldError } from "react-hook-form";
import { type IMaskInputProps, IMaskMixin } from "react-imask";

import type { InputLabelProps } from "Shared/Inputs/InputLabel";
import { InputWrapper } from "Shared/Inputs/InputWrapper";

export type MaskType = IMaskInputProps<any>["mask"];
export type MaskBlockType = Record<
  string,
  { mask: MaskType; from?: number; to?: number; lazy?: boolean }
>;

export interface BlInputLabelProps extends Omit<InputLabelProps, "htmlFor"> {}

export interface BlInputMaskProps {
  mask?: MaskType;
  /** IMask blocks configuration (per-block mask patterns). */
  blocks?: MaskBlockType;
  lazy?: boolean;
  unmask?: boolean | "typed";
  scale?: number;
  radix?: "." | ",";
  thousandsSeparator?: string;
  max?: number;
  min?: number;
  /** Fired with the accepted mask value (instead of onChange). */
  onMaskChange?: (val: string) => void;
  /** Auto-convert typed characters to uppercase. */
  uppercase?: boolean;
}

export interface BlInputProps
  extends Omit<TextFieldProps, "label" | "error" | "helperText"> {
  labelProps?: BlInputLabelProps;
  maskProps?: BlInputMaskProps;
  /** Displays a unit label as an end adornment (e.g. "kg", "Kč"). */
  unit?: string;
  /**
   * When true, always reserves space for the error message so the layout
   * never shifts when an error appears or disappears.
   */
  fixedHeight?: boolean;
  fieldError?: FieldError;
  /**
   * Shorthand for a numeric mask with sensible defaults:
   * mask: Number, radix: ",", thousandsSeparator: " ".
   * Any maskProps provided alongside override these defaults (e.g. scale, min, max).
   */
  isNumber?: boolean;
}

// Wraps TextField with IMask
const MaskedTextField = IMaskMixin(({ inputRef, ...props }: any) => (
  <TextField {...props} inputRef={inputRef} />
)) as any;

export const BlInput = ({
  labelProps,
  maskProps,
  isNumber,
  unit,
  fixedHeight,
  fieldError,
  name,
  value,
  onChange,
  slotProps,
  ...rest
}: BlInputProps): React.ReactNode => {
  const hasError = !!fieldError;

  const effectiveMaskProps: BlInputMaskProps | undefined = isNumber
    ? { mask: Number, radix: ",", thousandsSeparator: " ", ...maskProps }
    : maskProps;

  // Merge unit into endAdornment via slotProps
  const unitAdornment = unit ? (
    <Box
      component="span"
      sx={{
        fontSize: 12,
        fontWeight: 400,
        color: "text.secondary",
        pointerEvents: "none",
      }}
    >
      {unit}
    </Box>
  ) : undefined;

  const mergedSlotProps = unitAdornment
    ? {
        ...slotProps,
        input: {
          ...slotProps?.input,
          endAdornment: unitAdornment,
        },
      }
    : slotProps;

  return (
    <InputWrapper
      labelProps={
        labelProps?.label
          ? { ...labelProps, htmlFor: name, disabled: rest.disabled }
          : undefined
      }
      errorProps={{ error: fieldError, fixedHeight }}
    >
      {effectiveMaskProps?.mask ? (
        <MaskedTextField
          mask={effectiveMaskProps.mask}
          blocks={effectiveMaskProps.blocks}
          lazy={effectiveMaskProps.lazy ?? true}
          unmask={effectiveMaskProps.unmask ?? true}
          radix=","
          mapToRadix={["."]}
          thousandsSeparator={effectiveMaskProps.thousandsSeparator ?? " "}
          max={effectiveMaskProps.max ?? Number.MAX_SAFE_INTEGER}
          min={effectiveMaskProps.min ?? Number.MIN_SAFE_INTEGER}
          scale={effectiveMaskProps.scale}
          value={value}
          name={name}
          id={name}
          variant="outlined"
          fullWidth
          error={hasError}
          onAccept={(val: string) => {
            const transformed = effectiveMaskProps.uppercase
              ? val.toUpperCase()
              : val;
            effectiveMaskProps.onMaskChange?.(transformed);
          }}
          slotProps={mergedSlotProps}
          {...rest}
        />
      ) : (
        <TextField
          name={name}
          id={name}
          variant="outlined"
          fullWidth
          error={hasError}
          value={value}
          onChange={onChange}
          slotProps={mergedSlotProps}
          {...rest}
        />
      )}
    </InputWrapper>
  );
};
